import { colors } from "@permisso/permisso-components";
import { T } from "src/apps/components/i18n/Translation";
import { Icon, ICON_SIZES } from "src/apps/components/icon/Icon";
import { ICON_PATHS } from "src/apps/components/icon/IconSVGPaths";
import { Text } from "src/apps/components/typography/Texts";

import { useTheme } from "src/apps/providers/ThemeStore";
import { orderResponseErrorToAccentKey } from "src/contexts/documentationArea/application/OrderResponseErrorAccentKeys";
import { OrderResponse } from "src/contexts/documentationArea/domain/OrderResponse";
import { OrderTypes } from "src/contexts/documentationArea/domain/OrderTypes";
import { getResponseStatusMessageKey } from "./getResponseStatusMessageKey";
import styled from "styled-components";

const SHARED_KEYS = {
  shared_by_you: "documentation_area.order_box.response_item.shared_by_you",
  shared_by_name: "documentation_area.order_box.response_item.shared_by_name",
  shared_by_other: "documentation_area.order_box.response_item.shared_by_other",
} as const;

interface OrderBoxResponseItemProps {
  response: OrderResponse;
  orderType: OrderTypes;
  $isHidden?: boolean;
  isShared?: boolean;
  currentActorId?: string | null;
}

export const OrderBoxResponseItem = ({
  response,
  orderType,
  $isHidden = false,
  isShared = false,
  currentActorId,
}: OrderBoxResponseItemProps) => {
  const { errorColor, successColor } = useTheme();

  const fileName =
    response.getInputFiles()[0]?.name ??
    response.getOutputFiles().find((file) => file.extension === "pdf")?.name ??
    "Unknown file";

  const getSharedKey = () => {
    if (!isShared || !response.isValid()) return null;

    const uploadedBy = response.getRetrievalMethodOutput()?.uploadedBy;
    if (!uploadedBy) return null;

    if (uploadedBy.actorId === currentActorId) return SHARED_KEYS.shared_by_you;
    if (uploadedBy?.actorName) return SHARED_KEYS.shared_by_name;
    return SHARED_KEYS.shared_by_other;
  };

  const renderSharedDescription = () => {
    const key = getSharedKey();
    if (!key) return null;

    const actorName = response.getRetrievalMethodOutput()?.uploadedBy?.actorName ?? undefined;

    return (
      <SharedDescriptionText>
        <T k={key} params={{ actor_name: actorName }} />
      </SharedDescriptionText>
    );
  };

  const renderSubtitle = () => {
    if (response.isValid()) {
      return (
        <>
          <SubtitleText $color={successColor}>
            <T k={getResponseStatusMessageKey(orderType, response.getStatus())} />
          </SubtitleText>
          {renderSharedDescription()}
        </>
      );
    }

    if (response.isInvalid()) {
      const errorString = orderResponseErrorToAccentKey(response.getStatusMessages()[0]) ?? "";
      return (
        <SubtitleText $color={errorColor}>
          <T
            k={
              errorString
                ? errorString
                : "documentation_area.order_box.response_item.status.invalid"
            }
          />
        </SubtitleText>
      );
    }

    if (response.isPending()) {
      return (
        <SubtitleText $color={colors.grey700}>
          <T k={"documentation_area.order_box.response_item.status.pending"} />
        </SubtitleText>
      );
    }

    return null;
  };

  return (
    <Container $isHidden={$isHidden}>
      <Icon color={colors.grey700} path={ICON_PATHS.FILE_GENERIC} size={ICON_SIZES.s18} />
      <InfoContainer>
        <FileName $color={colors.grey700}>{fileName}</FileName>
        {renderSubtitle()}
      </InfoContainer>
    </Container>
  );
};

const Container = styled.div<{ $isHidden?: boolean }>`
  background-color: ${colors.grey200};
  border-radius: 0.375rem;
  padding: 0.5rem 1rem;
  gap: 1rem;
  display: ${(props) => (props.$isHidden ? "none" : "grid")};
  grid-template-columns: auto 1fr;
  align-items: center;
`;

const InfoContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  overflow: hidden;
  white-space: nowrap;
`;

const FileName = styled(Text)`
  font-size: 0.875rem;
  line-height: 1.3125rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SubtitleText = styled(Text)<{ $color: string }>`
  font-size: 0.75rem;
  line-height: 1rem;
  color: ${(props) => props.$color};
  text-overflow: ellipsis;
`;

const SharedDescriptionText = styled(Text)`
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.75rem;
  font-weight: 400;
  color: ${colors.grey700};
`;
